import React from 'react'
import { Button, Dialog, Fab, Typography, useMediaQuery } from '@mui/material'
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded'
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded'
import clsx from 'clsx'
import {
    useInitNotificationInAppStore,
    useCreateNotificationInAppStore,
    constants,
    BadgeOnNotification,
} from '@workspace/frontend-kit'
import UserMenuItem from './UserMenuItem'
import UserMenuItemGroup from './UserMenuItemGroup'
import { HELP_CENTER_URL } from '../../../../utils/constants'
import { logoutUser, triggerDemoModeInitiator } from '../../../../redux/actions'
import { useAppDispatch, useAppSelector } from '../../../../store'
import useTTDFlag from '../../../../hooks/useTTDFlag'
import useStyles from './styles/UserMenuStyles'
import packageJson from '../../../../../../package.json'
import { useUserMenuContext } from './UserMenuContext'
// import { useGetActPaymentRulesQuery } from '../../../../api/queries/__generated__/payment.gql'
import UserMenuSxs from './styles/UserMenuSxs'
import { CloseIcon, HelpIcon } from '../../../../../assets/icons'
import { localStorageManager } from '../../../../utils/LocalStorageManager'
import { UserMenuProps } from './_props'

const UserMenu = ({ onOpen }: UserMenuProps) => {
    const userStore = useAppSelector((state) => state.auth.user)
    const { initNotificationInAppStore } = useInitNotificationInAppStore()
    const { update, state } = useUserMenuContext()
    const dispatch = useAppDispatch()

    const { notifications } = useCreateNotificationInAppStore()

    React.useEffect(() => {
        initNotificationInAppStore(userStore.objectId, constants.NOTIFICATIONS_SOURCE.UPDATE_ANN)
    }, [userStore.objectId])

    // const {
    //     data: paymentRulesData,
    //     loading: isLoadingDisplayPayement,
    //     error: displayPayementError,
    // } = useGetActPaymentRulesQuery()
    // const canUsePayment = paymentRulesData?.getActPaymentRules?.canUsePayment

    // const displayPayement = !isLoadingDisplayPayement && !displayPayementError && canUsePayment

    const isDmpEnabled = useTTDFlag('dmp')
    // const isStripeEnabled = useTTDFlag('stripe')

    const openSourcesLicencesLink =
        'https://docs.google.com/spreadsheets/d/1M2qjAqDBB2YC5SWmLvcdAS1zY58kE-z4v5KyERHlf3E/edit'

    const matches = useMediaQuery('(min-width:600px)')
    const isMobile = !matches

    const triggerDemoMode = () => {
        update({
            isOpen: false,
        })
        dispatch(triggerDemoModeInitiator())
    }

    const changeMenuOpenState = () => {
        update({ isOpen: !state.isOpen })
    }

    const handleLogout = () => {
        dispatch(logoutUser())
        localStorageManager.clear()
        const hds = window._env_.REACT_APP_HDS_HOST
        const auth = window._env_.REACT_APP_CUR_AUTH_CLBC
        window.location.href = `${hds}/login?redirect=${auth}`
    }

    const classes = useStyles()
    const arrow = state.isOpen ? <ArrowDropUpRoundedIcon /> : <ArrowDropDownRoundedIcon />
    const displayBadge = notifications[constants.NOTIFICATIONS_SOURCE.UPDATE_ANN]?.displayBadge

    return (
        <div className={classes.UserMenu}>
            {/* arrow */}
            <Button
                // disabled={isLoadingDisplayPayement}
                onClick={() => changeMenuOpenState()}
                sx={UserMenuSxs.button}
                data-cy="secondaryMenuButton"
            >
                <span className={classes.UserMenuButtonName}>
                    <BadgeOnNotification displayBadge={displayBadge}>
                        <span
                            style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: isMobile ? '100px' : '200px',
                            }}
                        >
                            {userStore.fullName}
                        </span>
                    </BadgeOnNotification>
                </span>
                {/* {!isLoadingDisplayPayement ? arrow : <CircularProgress size="1rem" />} */}
                {arrow}
            </Button>
            <Dialog
                className={classes.UserMenuDialog}
                classes={{
                    paper: clsx(
                        classes.UserMenuDialogPaper,
                        !isMobile ? classes.UserMenuDialogPaperFloating : classes.UserMenuDialogPaperStatic,
                    ),
                }}
                onClose={changeMenuOpenState}
                aria-labelledby="simple-dialog-title"
                open={state.isOpen}
            >
                {isMobile && (
                    <Fab className={classes.UserMenuStaticCloseButtonIcon} onClick={changeMenuOpenState}>
                        <CloseIcon />
                    </Fab>
                )}
                {/* Menu items */}
                <div>
                    <UserMenuItemGroup label="Mon compte" separator={false} isOpen={displayBadge}>
                        <UserMenuItem
                            label="Mes informations"
                            action="/account"
                            separator={false}
                            displayBadge={displayBadge}
                        />
                        {/* disabled for now because it's unused */}
                        {/* {isStripeEnabled && displayPayement && (
                                <UserMenuItem label="Mes paiements en ligne" action="/payments" separator={false} />
                            )} */}
                    </UserMenuItemGroup>
                    <UserMenuItemGroup label="Configuration">
                        <UserMenuItem label="Comptes-rendus" action="/summary" separator={false} />
                        <UserMenuItem label="Motifs" action="/motives" enabled={false} separator={false} />
                        <UserMenuItem label="Ordonnances" action="/prescriptions" enabled={false} separator={false} />
                        <UserMenuItem label="Préférences utilisateur" action="/preferences" separator={false} enabled />
                    </UserMenuItemGroup>
                    <UserMenuItem label="Nouveautés" isExternal action={HELP_CENTER_URL} />
                    <UserMenuItem label="Mode démonstration" action={triggerDemoMode} />
                    <UserMenuItem label="Test de compatibilité" action="/compatibility" />
                    <UserMenuItemGroup label="Informations légales">
                        <UserMenuItem label="CGU" action="/cgu" separator={false} />
                        <UserMenuItem label="Mentions légales" action="/legals" separator={false} />
                        <UserMenuItem
                            label="Licences tierces open-source"
                            action={openSourcesLicencesLink}
                            isExternal
                            separator={false}
                        />
                        <UserMenuItem label="À Propos" action="/dmp" enabled={isDmpEnabled} separator={false} />
                    </UserMenuItemGroup>
                    <UserMenuItemGroup label="Aide" icon={<HelpIcon />}>
                        <UserMenuItem label="Besoin d'assistance" action={onOpen} separator={false} />
                    </UserMenuItemGroup>
                    {/* logout button */}
                    <div className={classes.UserMenuActionSeparator} />
                    <div className={clsx(classes.UserMenuMenuItem, classes.UserMenuMenuItemButton)}>
                        <Button
                            onClick={handleLogout}
                            disableElevation
                            variant="contained"
                            className={clsx(classes.UserMenuButton, classes.UserMenuButtonTypeLogout)}
                        >
                            Se déconnecter
                        </Button>
                    </div>
                    <div className={classes.UserMenuDialogInfos}>
                        <Typography className={classes.UserMenuDialogInfosVersion}>
                            {`TokTokDoc ${packageJson.version}`}
                        </Typography>
                    </div>
                </div>
            </Dialog>
        </div>
    )
}

export default UserMenu
